import * as React from "react";
import { useEffect, useState } from "react";
import { USER_ROLES } from "../enums";

interface UserData {
  entityId: string;
  entityName: string;
  email: string;
  name: string;
  mobile: string;
  whatsApp: string;
  status: "active" | "suspend";
  userRoleId?: number;
}

type ActiveTab = "corporate" | "project" | "kits";
interface EditUserProps {
  userData: UserData;
  onClose: () => void;
  onUpdateUser: (userData: UserData) => void;
  activeTab: ActiveTab;
  entityName?: string;
  projects?: any[];
  corporates?: any[];
}

type CountryCode = { code: string; name: string };

const getRoleOptions = (activeTab: "corporate" | "project" | "kits", numberOfLevels: number = 0) => {
  switch (activeTab) {
    case "corporate":
      return USER_ROLES.filter((role) => role.userRoleId === 100); // Corporate Admin
    case "project":
      let roles = USER_ROLES.filter((role) => role.userRoleId === 1000); // Project Admin
      if (numberOfLevels > 0) {
        const maintenanceRoles = USER_ROLES.filter(
          (role) => role.userRoleId >= 2000 && role.userRoleId < 7000
        ).slice(0, numberOfLevels); // take first N levels
        roles = [...roles, ...maintenanceRoles];
      }
      return roles;
    case "kits":
      return USER_ROLES.filter((role) => role.userRoleId === 10000); // Kit User
    default:
      return [];
  }
};

const EditUser: React.FC<EditUserProps> = ({
  userData,
  onClose,
  onUpdateUser,
  activeTab,
  entityName,
  projects = [],
  corporates = [],
}) => {
  // === NEW: dynamic country codes ===
  const [countryCodes, setCountryCodes] = useState<CountryCode[]>([]);
  const [codesLoading, setCodesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // Cache for a day to avoid refetching repeatedly
        const cacheKey = "countryCodesCache_v1";
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { at, data } = JSON.parse(cached) as { at: number; data: CountryCode[] };
          if (Date.now() - at < 24 * 60 * 60 * 1000) {
            if (!cancelled) {
              setCountryCodes(data);
              setCodesLoading(false);
            }
            return;
          }
        }

        const res = await fetch(
          "https://restcountries.com/v3.1/all?fields=name,idd"
        );
        if (!res.ok) throw new Error("Failed to fetch country codes");

        type RC = { name?: { common?: string }; idd?: { root?: string; suffixes?: string[] } };
        const raw: RC[] = await res.json();

        // Build unique code map
        const map = new Map<string, string>(); // code -> country name
        for (const c of raw) {
          const root = c.idd.root;
          const suffixes = c.idd.suffixes;
          const countryName = c.name.common;
          if (!root || !countryName) continue;

          if (Array.isArray(suffixes) && suffixes.length > 0) {
            for (const s of suffixes) {
              const code = `${root}${s}`;
              if (!map.has(code)) map.set(code, countryName);
            }
          } else {
            // Some records have root only (rare)
            const code = root;
            if (!map.has(code)) map.set(code, countryName);
          }
        }

        // Convert to array
        let arr: CountryCode[] = Array.from(map.entries()).map(([code, name]) => ({
          code,
          name: `${name} (${code})`,
        }));

        // Put common ones on top (your defaults)
        const PRIORITY = new Set(["+94", "+91", "+1", "+44"]);
        arr.sort((a, b) => {
          const ap = PRIORITY.has(a.code) ? 0 : 1;
          const bp = PRIORITY.has(b.code) ? 0 : 1;
          if (ap !== bp) return ap - bp;
          return a.name.localeCompare(b.name);
        });

        if (!cancelled) {
          setCountryCodes(arr);
          localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data: arr }));
        }
      } catch {
        // Fallback minimal list if API fails
        if (!cancelled) {
          setCountryCodes([
            { code: "+94", name: "Sri Lanka (+94)" },
            { code: "+91", name: "India (+91)" },
            { code: "+1", name: "USA (+1)" },
            { code: "+44", name: "UK (+44)" },
          ]);
        }
      } finally {
        if (!cancelled) setCodesLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // === Parse phone/whatsapp using the dynamically loaded codes ===
  const pickCodeAndNumber = (full: string): { countryCode: string; number: string } => {
    const clean = (full || "").replace(/\s+/g, "");
    const defaultResult = { countryCode: "+94", number: clean.replace(/^\+/, "") };

    if (!clean.startsWith("+") || countryCodes.length === 0) return defaultResult;

    // Match the longest code that prefixes the string
    const codesByLenDesc = [...countryCodes].sort((a, b) => b.code.length - a.code.length);
    const hit = codesByLenDesc.find((c) => clean.startsWith(c.code));
    if (hit) {
      return { countryCode: hit.code, number: clean.slice(hit.code.length) };
    }
    return defaultResult;
  };

  // form state
  const [formData, setFormData] = useState<UserData>(userData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({ email: "", name: "", phone: "" });

  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("+94");
  const [selectedWhatsAppCountryCode, setSelectedWhatsAppCountryCode] = useState<string>("+94");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [whatsAppNumber, setWhatsAppNumber] = useState<string>("");
  const [isUpdatedWhatsapp,setIsUpdatedWhatsapp]=useState(false);
  const [isUpdatedMobile,setIsUpdatedMobile]=useState(false);


  // Initialize phone fields once codes are ready (or on userData change)
  useEffect(() => {
    const p = pickCodeAndNumber(userData.mobile);
    setSelectedCountryCode(p.countryCode);
    setPhoneNumber(p.number);

    const w = pickCodeAndNumber(userData.whatsApp);
    setSelectedWhatsAppCountryCode(w.countryCode);
    setWhatsAppNumber(w.number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesLoading, userData.mobile, userData.whatsApp]); // re-run when codes finish loading

  const selectedProject = projects.find((project) => project.id === userData.entityId);
  const numberOfLevels = selectedProject ? selectedProject.maintainLevel : 0;
  const roleOptions = getRoleOptions(activeTab, numberOfLevels);

  const getEntityLabel = () => {
    switch (activeTab) {
      case "project":
        return "Project";
      case "kits":
        return "Kit";
      default:
        return "Corporate";
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Prevent non-digit input for phone fields (these two fields use dedicated handlers anyway)
    if ((name === "phone" || name === "whatsapp") && value && !/^\d*$/.test(value)) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name as keyof typeof errors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*$/.test(value)) return;
    setPhoneNumber(value);
    setIsUpdatedMobile(true);
  };

  const handleWhatsAppNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && !/^\d*$/.test(value)) return;
    setWhatsAppNumber(value);
    setIsUpdatedWhatsapp(true);
  };

  const handleCountryCodeChange = (e: React.ChangeEvent<HTMLSelectElement>, type: "phone" | "whatsapp") => {
    if (type === "phone") setSelectedCountryCode(e.target.value);
    else setSelectedWhatsAppCountryCode(e.target.value);
  };

  const handleStatusChange = (status: "active" | "suspend") => {
    setFormData((prev) => ({ ...prev, status }));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { email: "", name: "", phone: "" };

    // Name
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      valid = false;
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Name must be at least 2 characters";
      valid = false;
    } else if (formData.name.trim().length > 50) {
      newErrors.name = "Name cannot exceed 50 characters";
      valid = false;
    } else if (!/^[a-zA-Z\s]+$/.test(formData.name.trim())) {
      newErrors.name = "Name can only contain letters and spaces";
      valid = false;
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
      valid = false;
    } else if (formData.email.length > 100) {
      newErrors.email = "Email cannot exceed 100 characters";
      valid = false;
    }

    // Phone
    if (!phoneNumber.trim()) {
      newErrors.phone = "Phone number is required";
      valid = false;
    } else if (!/^\d+$/.test(phoneNumber)) {
      newErrors.phone = "Phone number must contain only digits";
      valid = false;
    } else if (phoneNumber.length < 9) {
      newErrors.phone = "Phone number must be at least 9 digits";
      valid = false;
    } else if (phoneNumber.length > 15) {
      newErrors.phone = "Phone number cannot exceed 15 digits";
      valid = false;
    }

    // WhatsApp optional (already numeric constrained in handler)

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      const updatedUserData = {
        ...formData,
        ...(isUpdatedMobile && phoneNumber && { mobile: `${selectedCountryCode}${phoneNumber}`, }),
        ...(isUpdatedWhatsapp && whatsAppNumber && {
          whatsApp: `${selectedWhatsAppCountryCode}${whatsAppNumber}`,
        }),
      };

      setLoading(true);
      await new Promise((r) => setTimeout(r, 1000)); // simulate API
      onUpdateUser(updatedUserData);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(50, 64, 84, 0.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 1000,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          width: "703px",
          borderRadius: "2px",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            backgroundColor: "#FFFFF",
            padding: "12px 32px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontSize: "20px",
              fontWeight: 600,
              color: "#324054",
              fontFamily: "'Poppins', sans-serif",
              margin: 0,
            }}
          >
            EDIT {activeTab.toUpperCase()} USER
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              fontSize: "24px",
              color: "#324054",
              fontWeight: "bold",
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div style={{ backgroundColor: "#f5f6f6ff", padding: "22px" }}>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {/* Entity */}
            <div>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "6px",
                }}
              >
                {getEntityLabel()}
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #E5E7EB",
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 400,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                  backgroundColor: "#f5f6f6ff",
                }}
                type="text"
                value={userData.entityName || entityName || "Unknown"}
                disabled
              />
            </div>

            {/* Email */}
            <div>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "6px",
                }}
              >
                Email
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${errors.email ? "#EF4444" : "#E5E7EB"}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 400,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                  backgroundColor: "#f5f6f6ff",
                }}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && (
                <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {errors.email}
                </span>
              )}
            </div>

            {/* Name */}
            <div>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "6px",
                }}
              >
                Name
              </label>
              <input
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${errors.name ? "#EF4444" : "#E5E7EB"}`,
                  borderRadius: "4px",
                  fontSize: "14px",
                  fontWeight: 400,
                  outline: "none",
                  fontFamily: "'Poppins', sans-serif",
                  boxSizing: "border-box",
                  color: "#324054",
                  backgroundColor: "#f5f6f6ff",
                }}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && (
                <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  {errors.name}
                </span>
              )}
            </div>

            {/* Phone & WhatsApp */}
            <div style={{ display: "flex", gap: "16px" }}>
              {/* Phone */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "6px",
                  }}
                >
                  Phone No.
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    style={{
                      width: "120px",
                      padding: "10px 8px",
                      border: `1px solid ${errors.phone ? "#EF4444" : "#E5E7EB"}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: 400,
                      outline: "none",
                      fontFamily: "'Poppins', sans-serif",
                      boxSizing: "border-box",
                      color: "#324054",
                      backgroundColor: "#f5f6f6ff",
                    }}
                    value={selectedCountryCode}
                    onChange={(e) => handleCountryCodeChange(e, "phone")}
                    disabled={codesLoading}
                  >
                    {codesLoading ? (
                      <option>Loading…</option>
                    ) : (
                      countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))
                    )}
                  </select>
                  <input
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: `1px solid ${errors.phone ? "#EF4444" : "#E5E7EB"}`,
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: 400,
                      outline: "none",
                      fontFamily: "'Poppins', sans-serif",
                      boxSizing: "border-box",
                      color: "#324054",
                      backgroundColor: "#f5f6f6ff",
                    }}
                    type="tel"
                    placeholder="07x xx xx xxx"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    pattern="[0-9]*"
                    inputMode="numeric"
                  />
                </div>
                {errors.phone && (
                  <span style={{ color: "#EF4444", fontSize: "12px", marginTop: "4px", display: "block" }}>
                    {errors.phone}
                  </span>
                )}
              </div>

              {/* WhatsApp */}
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    textAlign: "left",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#324054",
                    marginBottom: "6px",
                  }}
                >
                  WhatsApp No.
                </label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <select
                    style={{
                      width: "120px",
                      padding: "10px 8px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: 400,
                      outline: "none",
                      fontFamily: "'Poppins', sans-serif",
                      boxSizing: "border-box",
                      color: "#324054",
                      backgroundColor: "#f5f6f6ff",
                    }}
                    value={selectedWhatsAppCountryCode}
                    onChange={(e) => handleCountryCodeChange(e, "whatsapp")}
                    disabled={codesLoading}
                  >
                    {codesLoading ? (
                      <option>Loading…</option>
                    ) : (
                      countryCodes.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))
                    )}
                  </select>
                  <input
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      border: "1px solid #E5E7EB",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: 400,
                      outline: "none",
                      fontFamily: "'Poppins', sans-serif",
                      boxSizing: "border-box",
                      color: "#324054",
                      backgroundColor: "#f5f6f6ff",
                    }}
                    type="tel"
                    placeholder="07x xx xx xxx"
                    value={whatsAppNumber}
                    onChange={handleWhatsAppNumberChange}
                    pattern="[0-9]*"
                    inputMode="numeric"
                  />
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <label
                style={{
                  display: "block",
                  textAlign: "left",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#324054",
                  marginBottom: "6px",
                }}
              >
                Status
              </label>
              <div style={{ display: "flex", gap: "16px" }}>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
                  <input
                    type="radio"
                    name="status"
                    checked={formData.status === "active"}
                    onChange={() => handleStatusChange("active")}
                    style={{ cursor: "pointer" }}
                  />
                  <span>Active</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", fontSize: "14px" }}>
                  <input
                    type="radio"
                    name="status"
                    checked={formData.status === "suspend"}
                    onChange={() => handleStatusChange("suspend")}
                    style={{ cursor: "pointer" }}
                  />
                  <span>Suspend</span>
                </label>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
              <button
                type="button"
                style={{
                  padding: "10px 16px",
                  borderRadius: "4px",
                  fontWeight: 500,
                  fontSize: "14px",
                  cursor: "pointer",
                  minWidth: "120px",
                  fontFamily: "'Poppins', sans-serif",
                  backgroundColor: "#FFFFFF",
                  color: "#324054",
                  border: "1px solid #324054",
                }}
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  padding: "10px 16px",
                  borderRadius: "4px",
                  fontWeight: 500,
                  fontSize: "14px",
                  cursor: "pointer",
                  minWidth: "120px",
                  fontFamily: "'Poppins', sans-serif",
                  backgroundColor: "#2A7CED",
                  color: "#FFFFFF",
                  border: "none",
                  opacity: loading ? 0.7 : 1,
                }}
                disabled={loading}
              >
                {loading ? "Updating..." : "Update User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditUser;
